import React, { useEffect, useRef, useState } from "react";
import { Button } from "@KristianIV/common-ui";
import LoadingAnimation from "../animation/LoadingAnimation";
import { VerCenteredRow } from "../layout/Row";
import { Incident } from "../model/incident";
import { checkAudioExists, requestIncidentAudio } from "../services/audioSliceService";

// The device retries internally for up to 90s before uploading, plus upload time
// on a slow link, so the audio can take a couple of minutes to arrive
const RESPONSE_TIMEOUT_MS = 3 * 60 * 1000;
const POLL_INTERVAL_MS = 5 * 1000;

type Phase =
  | { name: "idle" }
  | { name: "requesting" }
  | { name: "waiting" }
  | { name: "result"; status: string; message?: string }
  | { name: "failed"; message: string };

const statusText = (status: string, message?: string) => {
  switch (status) {
    case "uploaded":
      return "Audio uploaded";
    case "partial":
      return "Audio uploaded, parts were missing";
    case "unavailable":
      return message ? message : "Audio not available on device";
    case "failed":
      return message ? message : "Device could not upload the audio";
    default:
      return message ? message : `Upload ${status}`;
  }
};

const StatusMessage = ({ text }: { text: string }) => (
  <p style={{ margin: 0, marginRight: "0.5rem", opacity: 0.8 }}>{text}</p>
);

/**
 * Asks the incident's device to upload the stored audio around the incident,
 * then polls storage until the slice appears and calls onAudioUploaded so the
 * parent can load it into the player.
 *
 * The device's reply in
 * commander/organisations/{orgId}/uploadedIncidents/{incidentId} is readable
 * (see ../firebase/uploadedIncidents.ts, used by AudioComparisonPage) and also
 * carries partial/unavailable/failed details - this button still polls storage
 * because it predates the listener and only needs uploaded-or-not.
 */
const RequestAudioButton = ({
  incident,
  jwtToken,
  onAudioUploaded,
}: {
  incident: Incident;
  jwtToken: string;
  onAudioUploaded: () => void;
}) => {
  const [phase, setPhase] = useState<Phase>({ name: "idle" });
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  const opusPath = incident.objectPath.replace(/\.wav$/, ".opus");

  const cleanup = () => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = undefined;
    }
  };

  // Stop polling if the item unmounts mid-request
  useEffect(() => cleanup, []);

  const pollForSlice = () => {
    const startedAt = Date.now();
    const interval = setInterval(() => {
      checkAudioExists(jwtToken, opusPath, (exists) => {
        if (exists) {
          cleanup();
          setPhase({ name: "result", status: "uploaded" });
          onAudioUploaded();
        } else if (Date.now() - startedAt > RESPONSE_TIMEOUT_MS) {
          cleanup();
          setPhase({ name: "failed", message: "No audio arrived, device may be offline" });
        }
      });
    }, POLL_INTERVAL_MS);
    cleanupRef.current = () => clearInterval(interval);
  };

  const request = () => {
    setPhase({ name: "requesting" });
    requestIncidentAudio(
      jwtToken,
      incident.id,
      () => {
        setPhase({ name: "waiting" });
        pollForSlice();
      },
      (errorMsg) => setPhase({ name: "failed", message: errorMsg }),
    );
  };

  if (phase.name === "requesting") {
    return <Button isLoading>Requesting...</Button>;
  }
  if (phase.name === "waiting") {
    return (
      <VerCenteredRow>
        <LoadingAnimation height={24} width={24} />
        <p style={{ margin: 0, marginLeft: "0.5rem" }}>Waiting for device...</p>
      </VerCenteredRow>
    );
  }
  return (
    <VerCenteredRow>
      {phase.name === "result" && (
        <StatusMessage text={statusText(phase.status, phase.message)} />
      )}
      {phase.name === "failed" && <StatusMessage text={phase.message} />}
      <Button onClick={request}>Request audio</Button>
    </VerCenteredRow>
  );
};

export default RequestAudioButton;
